import { useEffect, useMemo, useState } from "react";

import { useBuildingModelHeaderQuery } from "queries/useBuildingModelHeaderQuery";
import { getDataCoverageWarnings } from "utils/helpers";

// components
import Sidebar from "components/molecules/Sidebar";
import { useBuildingModelUid } from "hooks/useBuildingModelUid";
import { BuildingSidebarTab } from "utils/enums";
import BuildingConstruction from "./BuildingConstruction";
import BuildingEnergy from "./BuildingEnergy";
import BuildingEquipment from "./BuildingEquipment";

// styles
import "./BuildingSidebar.scss";
import BuildingSidebarTabs from "./BuildingSidebarTabs/BuildingSidebarTabs";

interface BuildingSidebarProps {
    show: boolean;
    onClose: () => void;
    buildingName: string;
}

function BuildingSidebar({ show, onClose, buildingName }: BuildingSidebarProps) {
    const [view, setView] = useState<BuildingSidebarTab>(BuildingSidebarTab.construction);
    const [agentPrefillEquipmentSurvey, setAgentPrefillEquipmentSurvey] = useState<Partial<EquipmentSurvey> | null>(
        null
    );
    const onClickView = (view: BuildingSidebarTab) => setView(view);

    const buildingModelUid = useBuildingModelUid();

    const { data: buildingHeaderData } = useBuildingModelHeaderQuery(buildingModelUid);

    const dataCoverageWarnings = useMemo(
        () => (buildingHeaderData?.dataCoverage ? getDataCoverageWarnings(buildingHeaderData.dataCoverage) : undefined),
        [buildingHeaderData]
    );

    useEffect(() => {
        const handler = (event: Event) => {
            const customEvent = event as CustomEvent<{ equipmentSurveyPrefill?: Partial<EquipmentSurvey> | null }>;
            setView(BuildingSidebarTab.equipment);
            setAgentPrefillEquipmentSurvey(customEvent.detail?.equipmentSurveyPrefill ?? null);
        };
        window.addEventListener("equipment-topology-prefill", handler);
        return () => window.removeEventListener("equipment-topology-prefill", handler);
    }, []);

    return (
        <Sidebar onCancel={onClose} sidebarOpen={show} title="Building profile">
            <BuildingSidebarTabs
                onClick={onClickView}
                view={view}
                dataCoverageWarnings={dataCoverageWarnings}
                hasEquipmentSizeAndYear={buildingHeaderData?.dataCoverage.equipmentDataHasSizeAndYear}
                equipmentDataUploaded={buildingHeaderData?.dataCoverage.equipmentDataUploaded}
            />

            {view === BuildingSidebarTab.energy && <BuildingEnergy buildingName={buildingName} />}

            {view === BuildingSidebarTab.equipment && (
                <BuildingEquipment
                    agentPrefillEquipmentSurvey={agentPrefillEquipmentSurvey}
                    onAgentPrefillConsumed={() => setAgentPrefillEquipmentSurvey(null)}
                />
            )}

            {view === BuildingSidebarTab.construction && <BuildingConstruction />}
        </Sidebar>
    );
}

export default BuildingSidebar;
